import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import apiClient from '../../utils/apiClient';
import server from '../../utils/serverConfig';
import {
    FileText, Search, RefreshCw, Maximize2, X, ArrowLeftRight,
    CheckCircle, AlertTriangle, AlertCircle, BarChart2,
    ChevronDown, ChevronRight, Download, Filter, Zap, Shield,
    Clock, Activity, Layers, ArrowUp, ArrowDown
} from 'lucide-react';
import AccountSelectDropdown from './components/AccountSelectDropdown';
import { matchAccountTrades, processAccountRecords, formatDisplayTime } from './utils/accountAuditMatcher';
import { FMT_DATETIME_IST, FMT_DATETIME_SHORT } from './utils/auditHelpers';
import TradeSignalValidationModal from './components/TradeSignalValidationModal';

export default function AccountComparisonView() {
    // Accounts list state
    const [accounts, setAccounts] = useState([]);
    const [accountsLoading, setAccountsLoading] = useState(false);

    // Selected Accounts
    const [accountA, setAccountA] = useState('');
    const [accountB, setAccountB] = useState('');

    // Filter & Mode state
    const [fromDate, setFromDate] = useState(() => {
        const d = new Date();
        return d.toISOString().split('T')[0];
    });
    const [toDate, setToDate] = useState(() => {
        const d = new Date();
        return d.toISOString().split('T')[0];
    });
    const [marketMode, setMarketMode] = useState('optimus'); // 'optimus' | 'pledge_plus' | 'all'

    // Comparison Results & Execution State
    const [comparisonData, setComparisonData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasFetched, setHasFetched] = useState(false);

    // UI Table State
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [collapsed, setCollapsed] = useState({ accountA: false, accountB: false });
    const [filters, setFilters] = useState({});
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [activeFilterColumn, setActiveFilterColumn] = useState(null);
    const [selectedTradeForModal, setSelectedTradeForModal] = useState(null);

    const filterMenuRef = useRef(null);

    // 1. Fetch available Accounts
    useEffect(() => {
        const fetchAccountsList = async () => {
            setAccountsLoading(true);
            try {
                const [mapRes, usersRes] = await Promise.allSettled([
                    apiClient.get(`${server.godseye.baseUrl}fyers-user-mapping`),
                    apiClient.get(`${server.godseye.baseUrl}get-fyers-users`)
                ]);

                const mapList = (mapRes.status === 'fulfilled' && mapRes.value?.data) ? mapRes.value.data : [];
                const usersList = (usersRes.status === 'fulfilled' && usersRes.value?.data) ? usersRes.value.data : [];

                const accMap = {};
                usersList.forEach(u => {
                    const id = u.client_id || u.clientId || u.fyers_id;
                    if (id) {
                        accMap[id.toLowerCase()] = {
                            client_id: id,
                            name: u.name || u.clientName || u.account_holder_name || '',
                            status: u.status || ''
                        };
                    }
                });

                mapList.forEach(m => {
                    const id = m.client_id || m.clientId;
                    if (id) {
                        const lowId = id.toLowerCase();
                        if (accMap[lowId]) {
                            accMap[lowId].name = accMap[lowId].name || m.name || '';
                            if (m.status !== undefined && m.status !== null && m.status !== '') {
                                accMap[lowId].status = m.status;
                            }
                        } else {
                            accMap[lowId] = {
                                client_id: id,
                                name: m.name || '',
                                status: m.status || ''
                            };
                        }
                    }
                });

                const combined = Object.values(accMap);

                // Filter ONLY active status accounts
                const activeAccounts = combined.filter(acc => {
                    const s = String(acc.status || '').trim().toLowerCase();
                    return s === 'active' || s === '1' || s === 'true' || s === 'enabled' || s === 'live';
                });

                // Fallback only if no account had explicit 'active' string
                const finalActiveAccounts = activeAccounts.length > 0
                    ? activeAccounts
                    : combined.filter(acc => {
                        const s = String(acc.status || '').trim().toLowerCase();
                        return !['inactive', 'disabled', 'dummy', 'paused', 'closed', 'suspended', '0', 'false'].includes(s);
                    });

                // Sort alphabetically by client_id
                finalActiveAccounts.sort((a, b) => a.client_id.localeCompare(b.client_id));

                setAccounts(finalActiveAccounts);

                // Auto-select first two active accounts if not set
                if (finalActiveAccounts.length >= 2 && !accountA && !accountB) {
                    setAccountA(finalActiveAccounts[0].client_id);
                    setAccountB(finalActiveAccounts[1].client_id);
                } else if (finalActiveAccounts.length === 1 && !accountA) {
                    setAccountA(finalActiveAccounts[0].client_id);
                }
            } catch (err) {
                console.error('Failed to load accounts list', err);
            } finally {
                setAccountsLoading(false);
            }
        };

        fetchAccountsList();
    }, []);

    // Close Dropdown on Click Outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) {
                setActiveFilterColumn(null);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Helper to get array of YYYY-MM-DD date strings in range
    const getDatesInRange = (startDateStr, endDateStr) => {
        const dates = [];
        let curr = new Date(startDateStr);
        const end = new Date(endDateStr);
        if (isNaN(curr.getTime()) || isNaN(end.getTime())) {
            return [startDateStr];
        }
        while (curr <= end && dates.length <= 31) {
            dates.push(curr.toISOString().split('T')[0]);
            curr.setDate(curr.getDate() + 1);
        }
        return dates.length > 0 ? dates : [startDateStr];
    };

    // 2. Fetch Trades strictly for a given client within date range
    const fetchTradesForAccount = async (clientId, accountLabel = 'A') => {
        if (!clientId) return [];
        const cleanTargetId = String(clientId).trim().toLowerCase();
        try {
            const dateList = getDatesInRange(fromDate, toDate);

            // Determine strategy assets based on market mode
            let targetAssets = [];
            const optimusAssets = ['Optimus Nifty', 'Optimus Banknifty', 'Optimus Finnifty', 'Optimus Midcapnifty', 'Optimus Sensex'];
            const pledgeAssets = ['Pledge+ CRUDEOIL', 'Pledge+ SILVER', 'Pledge+ GOLD', 'Pledge+ NATURALGAS', 'Pledge+ COPPER', 'Pledge+ ZINC', 'Pledge+ LEAD', 'Pledge+ ALUMINIUM'];

            if (marketMode === 'optimus') {
                targetAssets = optimusAssets;
            } else if (marketMode === 'pledge_plus') {
                targetAssets = pledgeAssets;
            } else {
                targetAssets = [...optimusAssets, ...pledgeAssets];
            }

            // Build queries for client audit on each date & strategy as well as range
            const clientAuditQueries = [];
            targetAssets.forEach(strat => {
                // Range query
                clientAuditQueries.push(
                    apiClient.get(`${server.godseye.baseUrl}audit-performance/client-audit?client_id=${encodeURIComponent(clientId)}&asset=${encodeURIComponent(strat)}&start_date=${fromDate}&end_date=${toDate}`)
                        .then(res => ({ type: 'client-audit', data: res.data, asset: strat }))
                        .catch(() => null)
                );

                // Per-date queries
                dateList.forEach(d => {
                    clientAuditQueries.push(
                        apiClient.get(`${server.godseye.baseUrl}audit-performance/client-audit?client_id=${encodeURIComponent(clientId)}&asset=${encodeURIComponent(strat)}&date=${d}`)
                            .then(res => ({ type: 'client-audit', data: res.data, asset: strat, date: d }))
                            .catch(() => null)
                    );
                });
            });

            // Also check buy-signal-audit and account trade history
            const generalQueries = [
                apiClient.get(`${server.godseye.baseUrl}audit-performance/buy-signal-audit/list?client_id=${encodeURIComponent(clientId)}&start_date=${fromDate}&end_date=${toDate}&limit=1000`)
                    .then(res => ({ type: 'buy-signal', data: res.data?.data || res.data }))
                    .catch(() => null),
                apiClient.get(`/api/get-trade-history-by-accountid/${encodeURIComponent(clientId)}`)
                    .then(res => ({ type: 'history', data: res.data }))
                    .catch(() => null)
            ];

            // Live trades if current day is included
            const todayStr = new Date().toISOString().split('T')[0];
            if (dateList.includes(todayStr)) {
                generalQueries.push(
                    apiClient.get(`${server.godseye.baseUrl}live_trades_data`)
                        .then(res => ({ type: 'live', data: res.data }))
                        .catch(() => null)
                );
            }

            const allResults = await Promise.allSettled([...clientAuditQueries, ...generalQueries]);
            let rawRecords = [];

            allResults.forEach(res => {
                if (res.status !== 'fulfilled' || !res.value) return;
                const { type, data, asset, date } = res.value;
                if (!data) return;

                let items = [];
                if (Array.isArray(data)) {
                    items = data;
                } else if (data.data && Array.isArray(data.data)) {
                    items = data.data;
                } else if (data.trades && Array.isArray(data.trades)) {
                    items = data.trades;
                }

                if (type === 'client-audit') {
                    // client-audit endpoint returns trades specifically for this client and strategy
                    items.forEach(item => {
                        rawRecords.push({
                            ...item,
                            client_id: clientId,
                            strategy: item.strategy_tag_l || item.strategy || asset,
                            trade_date: date
                        });
                    });
                } else {
                    // For general feeds, strictly require matching client_id
                    items.forEach(item => {
                        const itemClientId = String(item.client_id || item.clientId || item.client || '').trim().toLowerCase();
                        if (itemClientId === cleanTargetId) {
                            rawRecords.push(item);
                        }
                    });
                }
            });

            // Process and pair raw orders & trades for this client
            return processAccountRecords(rawRecords, clientId, accountLabel);
        } catch (err) {
            console.error(`Error fetching trades for account ${clientId}:`, err);
            return [];
        }
    };

    // 3. Compare Both Accounts
    const handleCompare = useCallback(async () => {
        if (!accountA || !accountB) {
            setError('Please select both Account A and Account B to perform comparison.');
            return;
        }
        if (accountA === accountB) {
            setError('Please select two distinct accounts to compare.');
            return;
        }

        setLoading(true);
        setError(null);
        setHasFetched(true);

        try {
            const [tradesA, tradesB] = await Promise.all([
                fetchTradesForAccount(accountA, 'A'),
                fetchTradesForAccount(accountB, 'B')
            ]);

            const infoA = accounts.find(a => a.client_id === accountA) || { client_id: accountA };
            const infoB = accounts.find(a => a.client_id === accountB) || { client_id: accountB };

            const paired = matchAccountTrades(tradesA, tradesB, infoA, infoB, {
                maxTimeDiffMinutes: 60,
                slippageTolerancePct: 0.5
            });

            setComparisonData(paired);
        } catch (err) {
            console.error('Account comparison failed', err);
            setError(err?.response?.data?.detail || err.message || 'Failed to compare account trades.');
        } finally {
            setLoading(false);
        }
    }, [accountA, accountB, fromDate, toDate, accounts]);

    // Swap Accounts
    const handleSwapAccounts = () => {
        const temp = accountA;
        setAccountA(accountB);
        setAccountB(temp);
    };

    // Sorting Handler
    const handleSort = (key, direction) => {
        if (sortConfig.key === key && sortConfig.direction === direction) {
            setSortConfig({ key: null, direction: 'asc' });
        } else {
            setSortConfig({ key, direction });
        }
    };

    // Filter Handler
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Filter by Market Mode (Optimus vs Pledge+)
    const isPledgeTrade = (row) => {
        const sym = (row.symbol || row.symbol_a || row.symbol_b || '').toUpperCase();
        const strat = (row.strategy_tag_a || row.strategy_tag_b || '').toUpperCase();
        if (sym.endsWith('CE') || sym.endsWith('PE') || /(\d+)(CE|PE)$/i.test(sym)) return false;

        const commodityTokens = [
            'GOLD', 'GOLDM', 'SILVER', 'SILVERM', 'SILVERMIC',
            'CRUDEOIL', 'CRUDEOILM', 'NATURALGAS', 'NATGASMINI', 'NATGAS',
            'COPPER', 'ALUMINIUM', 'ZINC', 'LEAD', 'NICKEL', 'MCX'
        ];
        const isCommodity = commodityTokens.some(c => sym === c || sym.startsWith(c) || sym.includes(c));

        return sym.includes('FUT') || isCommodity || strat.includes('PLEDGE') || strat.includes('COMMODITY');
    };

    // Filter & Sort Pipeline
    const processedData = useMemo(() => {
        let list = [...comparisonData];

        // Mode filter
        if (marketMode === 'optimus') {
            list = list.filter(r => !isPledgeTrade(r));
        } else if (marketMode === 'pledge_plus') {
            list = list.filter(r => isPledgeTrade(r));
        }

        // Column Filters
        list = list.filter(row => {
            return Object.entries(filters).every(([key, filterVal]) => {
                if (!filterVal) return true;
                const rowVal = row[key];
                if (rowVal === null || rowVal === undefined) return false;
                if (key === 'status') {
                    return String(rowVal).toLowerCase() === String(filterVal).toLowerCase();
                }
                return String(rowVal).toLowerCase().includes(filterVal.toLowerCase());
            });
        });

        // Sorting
        if (sortConfig.key) {
            list.sort((a, b) => {
                const aVal = a[sortConfig.key];
                const bVal = b[sortConfig.key];

                if (aVal === bVal) return 0;
                if (aVal === null || aVal === undefined) return 1;
                if (bVal === null || bVal === undefined) return -1;

                const isNum = !isNaN(parseFloat(aVal)) && isFinite(aVal) && !isNaN(parseFloat(bVal)) && isFinite(bVal);
                if (isNum) {
                    return sortConfig.direction === 'asc'
                        ? Number(aVal) - Number(bVal)
                        : Number(bVal) - Number(aVal);
                }

                return sortConfig.direction === 'asc'
                    ? String(aVal).localeCompare(String(bVal))
                    : String(bVal).localeCompare(String(aVal));
            });
        }

        return list;
    }, [comparisonData, marketMode, filters, sortConfig]);

    // Summary Metrics
    const metrics = useMemo(() => {
        const total = processedData.length;
        const matches = processedData.filter(d => d.status === 'Match').length;
        const mismatches = processedData.filter(d => d.status === 'Mismatch').length;
        const onlyA = processedData.filter(d => d.status === 'Account A Only').length;
        const onlyB = processedData.filter(d => d.status === 'Account B Only').length;

        // Calculate average delay among matched trades
        const validDelays = processedData
            .filter(d => d.time_diff_sec !== null && Math.abs(d.time_diff_sec) < 300)
            .map(d => Math.abs(d.time_diff_sec));
        const avgDelay = validDelays.length > 0
            ? (validDelays.reduce((sum, d) => sum + d, 0) / validDelays.length).toFixed(1)
            : '0.0';

        // Calculate average buy slippage %
        const validBuySlip = processedData
            .filter(d => d.slippage_buy !== null)
            .map(d => Math.abs(d.slippage_buy));
        const avgBuySlip = validBuySlip.length > 0
            ? (validBuySlip.reduce((sum, s) => sum + s, 0) / validBuySlip.length).toFixed(2)
            : '0.00';

        return { total, matches, mismatches, onlyA, onlyB, avgDelay, avgBuySlip };
    }, [processedData]);

    // CSV Export
    const handleExportCSV = () => {
        if (processedData.length === 0) return;
        const headers = [
            'Symbol', 'Account A', 'Strategy A', 'Buy Time A', 'Buy Price A', 'Sell Time A', 'Sell Price A', 'Qty A',
            'Account B', 'Strategy B', 'Buy Time B', 'Buy Price B', 'Sell Time B', 'Sell Price B', 'Qty B',
            'Status', 'Buy Slippage %', 'Sell Slippage %', 'Time Delta (sec)', 'Comments'
        ];

        const rows = processedData.map(r => [
            `"${r.symbol || ''}"`,
            `"${r.account_a || ''}"`,
            `"${r.strategy_tag_a || ''}"`,
            `"${r.buy_time_a || ''}"`,
            r.buy_price_a ?? '',
            `"${r.sell_time_a || ''}"`,
            r.sell_price_a ?? '',
            r.qty_a ?? '',
            `"${r.account_b || ''}"`,
            `"${r.strategy_tag_b || ''}"`,
            `"${r.buy_time_b || ''}"`,
            r.buy_price_b ?? '',
            `"${r.sell_time_b || ''}"`,
            r.sell_price_b ?? '',
            r.qty_b ?? '',
            `"${r.status || ''}"`,
            r.slippage_buy !== null ? r.slippage_buy.toFixed(2) : '',
            r.slippage_sell !== null ? r.slippage_sell.toFixed(2) : '',
            r.time_diff_sec !== null ? r.time_diff_sec.toFixed(1) : '',
            `"${(r.comments || '').replace(/"/g, '""')}"`
        ]);

        const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `Account_Comparison_${accountA}_vs_${accountB}_${fromDate}_${toDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Header Cell with interactive sorting & filtering
    const renderHeaderCell = (label, filterKey, type = 'text', className = '') => {
        const isActive = activeFilterColumn === filterKey;
        const isFiltered = !!filters[filterKey];
        const isSorted = sortConfig.key === filterKey;

        return (
            <th className={`px-3 py-2.5 shadow-[inset_0_-1px_0_0_#1F2937] border-gray-800 relative select-none ${className}`}>
                <div className="flex items-center justify-between gap-1 group">
                    <span
                        onClick={() => handleSort(filterKey, sortConfig.direction === 'asc' ? 'desc' : 'asc')}
                        className={`text-[11px] uppercase font-bold cursor-pointer hover:text-white transition-colors flex items-center gap-1 ${
                            isSorted ? 'text-blue-400' : 'text-gray-400'
                        }`}
                    >
                        {label}
                        {isSorted && (sortConfig.direction === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
                    </span>

                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setActiveFilterColumn(isActive ? null : filterKey);
                        }}
                        className={`p-1 rounded transition-colors ${
                            isFiltered ? 'text-blue-400 bg-blue-500/20' : 'text-gray-500 hover:text-gray-300'
                        }`}
                        title={`Filter ${label}`}
                    >
                        <Filter size={11} />
                    </button>

                    {/* Filter Popup Menu */}
                    {isActive && (
                        <div
                            ref={filterMenuRef}
                            className="absolute top-full left-0 mt-1 w-56 bg-[#1a1d29] border border-gray-700 rounded-xl shadow-2xl p-2.5 z-50 animate-in fade-in zoom-in-95 duration-100"
                        >
                            <div className="flex justify-between items-center mb-2 pb-1.5 border-b border-gray-800">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Filter {label}</span>
                                <button
                                    onClick={() => setActiveFilterColumn(null)}
                                    className="text-gray-500 hover:text-white p-0.5"
                                >
                                    <X size={12} />
                                </button>
                            </div>

                            {type === 'status' ? (
                                <div className="flex flex-col gap-1">
                                    {['', 'Match', 'Mismatch', 'Account A Only', 'Account B Only'].map((option) => (
                                        <button
                                            key={option}
                                            type="button"
                                            onClick={() => {
                                                handleFilterChange(filterKey, option);
                                                setActiveFilterColumn(null);
                                            }}
                                            className={`text-left px-2 py-1.5 rounded text-xs transition-colors flex items-center justify-between ${
                                                (filters[filterKey] || '') === option
                                                    ? 'bg-blue-600/30 text-blue-300 font-semibold'
                                                    : 'hover:bg-gray-800 text-gray-300'
                                            }`}
                                        >
                                            <span>{option || 'All Statuses'}</span>
                                            {(filters[filterKey] || '') === option && <CheckCircle size={12} />}
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="relative">
                                    <Search size={12} className="absolute left-2.5 top-2.5 text-gray-500" />
                                    <input
                                        type="text"
                                        placeholder={`Search ${label}...`}
                                        className="bg-[#0f1116] border border-gray-700 rounded-md w-full pl-8 pr-7 py-1.5 text-xs text-gray-200 placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                                        value={filters[filterKey] || ''}
                                        onChange={(e) => handleFilterChange(filterKey, e.target.value)}
                                        autoFocus
                                    />
                                    {filters[filterKey] && (
                                        <button
                                            onClick={() => handleFilterChange(filterKey, '')}
                                            className="absolute right-2 top-2 text-gray-500 hover:text-white p-0.5"
                                        >
                                            <X size={12} />
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </th>
        );
    };

    return (
        <div className="flex flex-col h-full gap-3 relative" style={{ overflow: 'visible' }}>
            {/* ── Controls Bar (compact: Date Range + Scope + Compare only) ── */}
            <div className="bg-[#1a1d29] border border-gray-800 rounded-xl shrink-0">
                <div className="flex items-end gap-2 px-3 py-2.5 flex-wrap xl:flex-nowrap gap-y-2">

                    {/* ── RIGHT SIDE: Date Range + Asset Scope + Compare ── */}
                    <div className="ml-auto flex items-end gap-2 flex-wrap gap-y-2">

                        {/* DATE RANGE */}
                        <div className="flex flex-col gap-1">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-0.5">Date Range</span>
                            <div className="flex items-center gap-1.5 bg-[#14161f] px-2 py-1.5 rounded-lg border border-gray-700/80 hover:border-gray-600 transition-colors">
                                <input
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    style={{ colorScheme: 'dark' }}
                                    className="px-1.5 py-0.5 bg-transparent text-xs font-semibold text-gray-300 outline-none cursor-pointer"
                                />
                                <span className="text-gray-600 text-xs">–</span>
                                <input
                                    type="date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    style={{ colorScheme: 'dark' }}
                                    className="px-1.5 py-0.5 bg-transparent text-xs font-semibold text-gray-300 outline-none cursor-pointer"
                                />
                            </div>
                        </div>

                        {/* ASSET SCOPE */}
                        <div className="flex flex-col gap-1">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-0.5">Asset Scope</span>
                            <div className="flex items-center gap-1 bg-[#14161f] p-1 rounded-lg border border-gray-700/80">
                                <button type="button" onClick={() => setMarketMode('all')}
                                    className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer ${marketMode === 'all' ? 'bg-gray-600 text-white shadow-sm' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'}`}>
                                    All
                                </button>
                                <button type="button" onClick={() => setMarketMode('optimus')}
                                    className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer ${marketMode === 'optimus' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'}`}>
                                    <Zap className="w-3 h-3 text-yellow-300" /> Options
                                </button>
                                <button type="button" onClick={() => setMarketMode('pledge_plus')}
                                    className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer ${marketMode === 'pledge_plus' ? 'bg-amber-600 text-white shadow-sm' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'}`}>
                                    <Shield className="w-3 h-3 text-amber-300" /> Futures
                                </button>
                            </div>
                        </div>

                        {/* COMPARE BUTTON */}
                        <button
                            type="button"
                            onClick={handleCompare}
                            disabled={loading || !accountA || !accountB}
                            className="self-end px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-lg text-xs font-bold flex items-center gap-2 shadow-md transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                        >
                            {loading ? (
                                <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Comparing...</>
                            ) : (
                                <><Search className="w-3.5 h-3.5" /> Compare Accounts</>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-500/10 border border-red-500/30 p-3 rounded-xl text-xs text-red-400 flex items-center gap-2 shrink-0">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {/* Summary KPI Metrics */}
            {hasFetched && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 shrink-0">
                    <div className="bg-[#1a1d29] border border-gray-800 rounded-xl p-3 flex flex-col">
                        <span className="text-[10px] uppercase font-bold text-gray-400">Total Analyzed</span>
                        <div className="text-xl font-bold text-gray-100 font-mono mt-1">{metrics.total}</div>
                        <span className="text-[10px] text-gray-500 mt-0.5">paired trades</span>
                    </div>

                    <div className="bg-[#1a1d29] border border-emerald-500/20 bg-emerald-500/5 rounded-xl p-3 flex flex-col">
                        <span className="text-[10px] uppercase font-bold text-emerald-400 flex items-center gap-1">
                            <CheckCircle size={12} /> Matches
                        </span>
                        <div className="text-xl font-bold text-emerald-300 font-mono mt-1">{metrics.matches}</div>
                        <span className="text-[10px] text-emerald-400/70 mt-0.5">synced executions</span>
                    </div>

                    <div className="bg-[#1a1d29] border border-amber-500/20 bg-amber-500/5 rounded-xl p-3 flex flex-col">
                        <span className="text-[10px] uppercase font-bold text-amber-400 flex items-center gap-1">
                            <AlertTriangle size={12} /> Mismatches
                        </span>
                        <div className="text-xl font-bold text-amber-300 font-mono mt-1">{metrics.mismatches}</div>
                        <span className="text-[10px] text-amber-400/70 mt-0.5">price/qty slippage</span>
                    </div>

                    <div className="bg-[#1a1d29] border border-blue-500/20 bg-blue-500/5 rounded-xl p-3 flex flex-col">
                        <span className="text-[10px] uppercase font-bold text-blue-400">{accountA || 'Account A'} Only</span>
                        <div className="text-xl font-bold text-blue-300 font-mono mt-1">{metrics.onlyA}</div>
                        <span className="text-[10px] text-blue-400/70 mt-0.5">missed by Account B</span>
                    </div>

                    <div className="bg-[#1a1d29] border border-purple-500/20 bg-purple-500/5 rounded-xl p-3 flex flex-col">
                        <span className="text-[10px] uppercase font-bold text-purple-400">{accountB || 'Account B'} Only</span>
                        <div className="text-xl font-bold text-purple-300 font-mono mt-1">{metrics.onlyB}</div>
                        <span className="text-[10px] text-purple-400/70 mt-0.5">missed by Account A</span>
                    </div>

                    <div className="bg-[#1a1d29] border border-gray-800 rounded-xl p-3 flex flex-col">
                        <span className="text-[10px] uppercase font-bold text-cyan-400 flex items-center gap-1">
                            <Clock size={12} /> Avg Timing Lag
                        </span>
                        <div className="text-xl font-bold text-cyan-300 font-mono mt-1">{metrics.avgDelay}s</div>
                        <span className="text-[10px] text-gray-500 mt-0.5">avg slip: {metrics.avgBuySlip}%</span>
                    </div>
                </div>
            )}

            {/* Results Table Card */}
            <div className={`flex flex-col bg-[#1a1d29] border border-gray-800 shadow-xl overflow-hidden transition-all duration-300 ${
                isFullScreen ? 'fixed inset-0 z-50 m-0 rounded-none' : 'flex-1 rounded-xl'
            }`}>
                {/* Table Header Bar */}
                <div className="px-5 py-3 border-b border-gray-800 flex justify-between items-center bg-[#14161f] shrink-0">
                    <div className="flex items-center gap-3">
                        <h3 className="text-xs font-bold text-gray-200 uppercase tracking-wider flex items-center gap-2">
                            <FileText className="w-4 h-4 text-purple-400" />
                            Account Comparison Log
                        </h3>
                        <span className="text-xs text-purple-400 bg-purple-500/10 border border-purple-500/30 px-2.5 py-0.5 rounded-lg font-mono font-semibold">
                            {processedData.length} Record(s) Loaded
                        </span>
                    </div>

                    <div className="flex items-center gap-2">
                        {processedData.length > 0 && (
                            <button
                                type="button"
                                onClick={handleExportCSV}
                                className="p-1.5 px-3 text-xs font-semibold text-gray-300 hover:text-white bg-gray-800/80 hover:bg-gray-700 border border-gray-700 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer"
                                title="Export table to CSV"
                            >
                                <Download className="w-3.5 h-3.5 text-blue-400" />
                                <span>Export CSV</span>
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => setIsFullScreen(!isFullScreen)}
                            className={`p-1.5 px-3 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 border cursor-pointer ${
                                isFullScreen
                                    ? 'bg-blue-600 border-blue-500 text-white shadow-md'
                                    : 'text-gray-300 hover:text-white bg-gray-800/80 hover:bg-gray-700 border-gray-700'
                            }`}
                        >
                            {isFullScreen ? <X className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5 text-blue-400" />}
                            <span>{isFullScreen ? 'Exit Fullscreen' : 'Fullscreen'}</span>
                        </button>
                        <button
                            type="button"
                            onClick={handleCompare}
                            disabled={loading || !accountA || !accountB}
                            className="p-1.5 px-3 text-xs font-semibold text-gray-300 hover:text-white bg-gray-800/80 hover:bg-gray-700 border border-gray-700 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 text-emerald-400 ${loading ? 'animate-spin' : ''}`} />
                            <span>Refresh</span>
                        </button>
                    </div>
                </div>

                {/* Table Container */}
                <div className="flex-1 overflow-auto custom-scrollbar">
                    <table className="w-full text-left border-collapse min-w-[1500px]">
                        <thead className="bg-[#14161f] text-gray-400 sticky top-0 z-50 shadow-sm border-b border-gray-800" style={{ overflow: 'visible' }}>
                            {/* Group Header Row */}
                            <tr>
                                <th className="px-3 py-2 border-b border-r border-[#1F2937] bg-[#14161f] w-[45px] text-center text-xs font-bold text-gray-500">
                                    #
                                </th>
                                <th className="px-3 py-2 border-b border-r border-[#1F2937] bg-[#14161f] w-[55px] text-center text-xs font-bold text-blue-400 uppercase tracking-wider">
                                    CHART
                                </th>

                                {/* Account A Group Header — contains searchable dropdown */}
                                <th
                                    className={`px-3 py-1.5 border-b border-r border-[#1F2937] transition-colors ${
                                        collapsed.accountA ? 'bg-[#1a1d29]/80' : 'bg-[#1a2040]/60'
                                    }`}
                                    colSpan={collapsed.accountA ? 2 : 7}
                                >
                                    <div className="flex items-center gap-2">
                                        {/* Collapse toggle */}
                                        <button
                                            type="button"
                                            onClick={() => setCollapsed(prev => ({ ...prev, accountA: !prev.accountA }))}
                                            className="flex items-center gap-1.5 text-blue-400 hover:text-blue-300 transition-colors cursor-pointer shrink-0"
                                            title={collapsed.accountA ? 'Expand Account A columns' : 'Collapse Account A columns'}
                                        >
                                            {collapsed.accountA ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                                            <span className="font-bold text-[10px] uppercase tracking-widest whitespace-nowrap">Account A</span>
                                        </button>
                                        {/* Inline Account A dropdown */}
                                        <div onClick={(e) => e.stopPropagation()} className="flex-1 min-w-0">
                                            <AccountSelectDropdown
                                                accounts={accounts}
                                                value={accountA}
                                                onChange={setAccountA}
                                                label=""
                                                badgeColor="blue"
                                                placeholder="Select Account A..."
                                                disabled={loading}
                                                compact={true}
                                            />
                                        </div>
                                        {/* Swap */}
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); handleSwapAccounts(); }}
                                            disabled={loading || !accountA || !accountB}
                                            className="p-1 shrink-0 bg-[#0f1116] border border-gray-700 hover:border-blue-500/60 rounded-md text-gray-400 hover:text-blue-300 transition-all disabled:opacity-30 cursor-pointer active:scale-90"
                                            title="Swap accounts"
                                        >
                                            <ArrowLeftRight className="w-3 h-3" />
                                        </button>
                                    </div>
                                </th>

                                {/* Account B Group Header — contains searchable dropdown */}
                                <th
                                    className={`px-3 py-1.5 border-b border-r border-[#1F2937] transition-colors ${
                                        collapsed.accountB ? 'bg-[#14161f]' : 'bg-[#1a1430]/60'
                                    }`}
                                    colSpan={collapsed.accountB ? 2 : 7}
                                >
                                    <div className="flex items-center gap-2">
                                        {/* Collapse toggle */}
                                        <button
                                            type="button"
                                            onClick={() => setCollapsed(prev => ({ ...prev, accountB: !prev.accountB }))}
                                            className="flex items-center gap-1.5 text-purple-400 hover:text-purple-300 transition-colors cursor-pointer shrink-0"
                                            title={collapsed.accountB ? 'Expand Account B columns' : 'Collapse Account B columns'}
                                        >
                                            {collapsed.accountB ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                                            <span className="font-bold text-[10px] uppercase tracking-widest whitespace-nowrap">Account B</span>
                                        </button>
                                        {/* Inline Account B dropdown */}
                                        <div onClick={(e) => e.stopPropagation()} className="flex-1 min-w-0">
                                            <AccountSelectDropdown
                                                accounts={accounts}
                                                value={accountB}
                                                onChange={setAccountB}
                                                label=""
                                                badgeColor="purple"
                                                placeholder="Select Account B..."
                                                disabled={loading}
                                                compact={true}
                                            />
                                        </div>
                                    </div>
                                </th>

                                {/* Validation & Slippage Audit Group */}
                                <th className="px-4 py-2 border-b border-[#1F2937] bg-[#14161f] text-center" colSpan={5}>
                                    <span className="font-bold text-xs uppercase tracking-wider text-amber-400">
                                        Validation &amp; Slippage Audit
                                    </span>
                                </th>
                            </tr>

                            {/* Column Header Row */}
                            <tr>
                                <th className="px-3 py-2.5 shadow-[inset_0_-1px_0_0_#1F2937] border-r border-[#1F2937] text-center w-[45px] text-[10px] font-bold text-gray-500">
                                    #
                                </th>
                                <th className="px-3 py-2.5 shadow-[inset_0_-1px_0_0_#1F2937] border-r border-[#1F2937] text-center w-[55px] text-[10px] font-bold text-blue-400 uppercase">
                                    CHART
                                </th>

                                {/* Account A Columns */}
                                {renderHeaderCell('Symbol', 'symbol_a', 'text', 'bg-[#1a1d29]/30 min-w-[150px]')}
                                {renderHeaderCell('Strategy', 'strategy_tag_a', 'text', 'bg-[#1a1d29]/30 min-w-[130px]')}
                                {!collapsed.accountA && (
                                    <>
                                        {renderHeaderCell('Buy Time', 'buy_time_a', 'text', 'bg-[#1a1d29]/30 min-w-[135px]')}
                                        {renderHeaderCell('Buy Price', 'buy_price_a', 'number', 'bg-[#1a1d29]/30 text-right min-w-[95px]')}
                                        {renderHeaderCell('Sell Time', 'sell_time_a', 'text', 'bg-[#1a1d29]/30 min-w-[135px]')}
                                        {renderHeaderCell('Sell Price', 'sell_price_a', 'number', 'bg-[#1a1d29]/30 text-right min-w-[95px]')}
                                        {renderHeaderCell('Qty', 'qty_a', 'number', 'bg-[#1a1d29]/30 text-right min-w-[70px] border-r border-[#1F2937]')}
                                    </>
                                )}

                                {/* Account B Columns */}
                                {renderHeaderCell('Symbol', 'symbol_b', 'text', 'bg-[#14161f] min-w-[150px]')}
                                {renderHeaderCell('Strategy', 'strategy_tag_b', 'text', 'bg-[#14161f] min-w-[130px]')}
                                {!collapsed.accountB && (
                                    <>
                                        {renderHeaderCell('Buy Time', 'buy_time_b', 'text', 'bg-[#14161f] min-w-[135px]')}
                                        {renderHeaderCell('Buy Price', 'buy_price_b', 'number', 'bg-[#14161f] text-right min-w-[95px]')}
                                        {renderHeaderCell('Sell Time', 'sell_time_b', 'text', 'bg-[#14161f] min-w-[135px]')}
                                        {renderHeaderCell('Sell Price', 'sell_price_b', 'number', 'bg-[#14161f] text-right min-w-[95px]')}
                                        {renderHeaderCell('Qty', 'qty_b', 'number', 'bg-[#14161f] text-right min-w-[70px] border-r border-[#1F2937]')}
                                    </>
                                )}

                                {/* Validation & Slippage Columns */}
                                {renderHeaderCell('Status', 'status', 'status', 'text-center w-[120px]')}
                                {renderHeaderCell('Buy Slip %', 'slippage_buy', 'number', 'text-right w-[95px]')}
                                {renderHeaderCell('Sell Slip %', 'slippage_sell', 'number', 'text-right w-[95px]')}
                                {renderHeaderCell('Delay (s)', 'time_diff_sec', 'number', 'text-right w-[85px]')}
                                {renderHeaderCell('Comments', 'comments', 'text', 'min-w-[220px]')}
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-gray-800/50 text-xs text-gray-300">
                            {processedData.length === 0 ? (
                                <tr>
                                    <td colSpan={21} className="text-center py-16 text-gray-500">
                                        {!hasFetched ? (
                                            <div className="flex flex-col items-center gap-2">
                                                <ArrowLeftRight className="w-8 h-8 text-gray-600" />
                                                <span className="text-sm font-semibold text-gray-400">Ready to Compare Accounts</span>
                                                <span className="text-xs text-gray-500">
                                                    Select Account A, Account B and click "Compare Accounts".
                                                </span>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center gap-2">
                                                <Search className="w-8 h-8 text-gray-600" />
                                                <span className="text-sm font-semibold text-gray-400">No trades match your criteria</span>
                                                <span className="text-xs text-gray-500">
                                                    Try adjusting the date range, accounts, or active column filters.
                                                </span>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ) : (
                                processedData.map((row, index) => {
                                    const isMatch = row.status === 'Match';
                                    const isOnlyA = row.status === 'Account A Only';
                                    const isOnlyB = row.status === 'Account B Only';
                                    const isMismatch = row.status === 'Mismatch';

                                    let rowClass = 'hover:bg-[#202330] transition-colors border-l-2 border-transparent';
                                    if (isMatch) rowClass = 'bg-emerald-500/5 hover:bg-emerald-500/10 text-emerald-100 border-l-2 border-emerald-500';
                                    else if (isOnlyA || isOnlyB) rowClass = 'bg-red-500/10 hover:bg-red-500/20 text-red-100 border-l-2 border-red-500';
                                    else if (isMismatch) rowClass = 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-100 border-l-2 border-amber-500';

                                    return (
                                        <tr key={row.id || index} className={rowClass}>
                                            {/* Index */}
                                            <td className="px-3 py-2 text-center text-gray-500 border-r border-gray-800/40 font-mono">
                                                {index + 1}
                                            </td>

                                            {/* Chart Icon */}
                                            <td className="px-3 py-2 text-center border-r border-gray-800/40">
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedTradeForModal(row)}
                                                    className="p-1 rounded bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 hover:text-blue-300 transition-colors cursor-pointer"
                                                    title="Open Trade Chart Validation"
                                                >
                                                    <BarChart2 size={13} />
                                                </button>
                                            </td>

                                            {/* Account A Data */}
                                            <td className="px-3 py-2 font-medium bg-[#1a1d29]/20">
                                                {row.symbol_a ? (
                                                    <span className="font-mono text-blue-200">{row.symbol_a}</span>
                                                ) : (
                                                    <span className="text-gray-600">-</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-blue-300/80 bg-[#1a1d29]/20 truncate max-w-[130px]">
                                                {row.strategy_tag_a || '-'}
                                            </td>
                                            {!collapsed.accountA && (
                                                <>
                                                    <td className="px-3 py-2 text-gray-400 font-mono text-[11px] bg-[#1a1d29]/20">
                                                        {formatDisplayTime(row.buy_time_a)}
                                                    </td>
                                                    <td className="px-3 py-2 text-right font-mono text-blue-300 bg-[#1a1d29]/20">
                                                        {row.buy_price_a !== null ? Number(row.buy_price_a).toFixed(2) : '-'}
                                                    </td>
                                                    <td className="px-3 py-2 text-gray-400 font-mono text-[11px] bg-[#1a1d29]/20">
                                                        {formatDisplayTime(row.sell_time_a)}
                                                    </td>
                                                    <td className="px-3 py-2 text-right font-mono text-blue-300 bg-[#1a1d29]/20">
                                                        {row.sell_price_a !== null ? Number(row.sell_price_a).toFixed(2) : '-'}
                                                    </td>
                                                    <td className="px-3 py-2 text-right font-mono text-gray-300 bg-[#1a1d29]/20 border-r border-gray-800/40">
                                                        {row.qty_a !== null ? row.qty_a : '-'}
                                                    </td>
                                                </>
                                            )}

                                            {/* Account B Data */}
                                            <td className="px-3 py-2 font-medium bg-[#14161f]/30">
                                                {row.symbol_b ? (
                                                    <span className="font-mono text-purple-200">{row.symbol_b}</span>
                                                ) : (
                                                    <span className="text-gray-600">-</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-purple-300/80 bg-[#14161f]/30 truncate max-w-[130px]">
                                                {row.strategy_tag_b || '-'}
                                            </td>
                                            {!collapsed.accountB && (
                                                <>
                                                    <td className="px-3 py-2 text-gray-400 font-mono text-[11px] bg-[#14161f]/30">
                                                        {formatDisplayTime(row.buy_time_b)}
                                                    </td>
                                                    <td className="px-3 py-2 text-right font-mono text-purple-300 bg-[#14161f]/30">
                                                        {row.buy_price_b !== null ? Number(row.buy_price_b).toFixed(2) : '-'}
                                                    </td>
                                                    <td className="px-3 py-2 text-gray-400 font-mono text-[11px] bg-[#14161f]/30">
                                                        {formatDisplayTime(row.sell_time_b)}
                                                    </td>
                                                    <td className="px-3 py-2 text-right font-mono text-purple-300 bg-[#14161f]/30">
                                                        {row.sell_price_b !== null ? Number(row.sell_price_b).toFixed(2) : '-'}
                                                    </td>
                                                    <td className="px-3 py-2 text-right font-mono text-gray-300 bg-[#14161f]/30 border-r border-gray-800/40">
                                                        {row.qty_b !== null ? row.qty_b : '-'}
                                                    </td>
                                                </>
                                            )}

                                            {/* Status Badge */}
                                            <td className="px-3 py-2 text-center">
                                                <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-bold border ${
                                                    isMatch
                                                        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                                                        : isMismatch
                                                        ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                                                        : 'bg-red-500/10 text-red-400 border-red-500/30'
                                                }`}>
                                                    {row.status}
                                                </span>
                                            </td>

                                            {/* Buy Slippage % */}
                                            <td className="px-3 py-2 text-right font-mono font-semibold">
                                                {row.slippage_buy !== null ? (
                                                    <span className={Math.abs(row.slippage_buy) <= 0.5 ? 'text-emerald-400' : 'text-amber-400'}>
                                                        {row.slippage_buy > 0 ? '+' : ''}{row.slippage_buy.toFixed(2)}%
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-600">-</span>
                                                )}
                                            </td>

                                            {/* Sell Slippage % */}
                                            <td className="px-3 py-2 text-right font-mono font-semibold">
                                                {row.slippage_sell !== null ? (
                                                    <span className={Math.abs(row.slippage_sell) <= 0.5 ? 'text-emerald-400' : 'text-amber-400'}>
                                                        {row.slippage_sell > 0 ? '+' : ''}{row.slippage_sell.toFixed(2)}%
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-600">-</span>
                                                )}
                                            </td>

                                            {/* Timing Delay */}
                                            <td className="px-3 py-2 text-right font-mono">
                                                {row.time_diff_sec !== null ? (
                                                    <span className={Math.abs(row.time_diff_sec) <= 3 ? 'text-gray-300' : 'text-amber-400'}>
                                                        {row.time_diff_sec > 0 ? '+' : ''}{row.time_diff_sec.toFixed(1)}s
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-600">-</span>
                                                )}
                                            </td>

                                            {/* Comments */}
                                            <td className="px-3 py-2 text-gray-400 truncate max-w-[280px]" title={row.comments}>
                                                {row.comments || '-'}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Trade Signal Validation Modal */}
            {selectedTradeForModal && (
                <TradeSignalValidationModal
                    tradeRecord={selectedTradeForModal}
                    isOpen={!!selectedTradeForModal}
                    onClose={() => setSelectedTradeForModal(null)}
                />
            )}
        </div>
    );
}
